import { clampRate } from './actuator.js';
import type { ControlFrame } from './messages.js';

/**
 * Pure input-mapping math: held actions → command vector. Lives in shared so
 * the console, the agent tests, and any future gamepad mapping all agree on
 * exactly one definition of "what the operator is asking for".
 */

export const CONTROL_ACTIONS = [
  'forward',
  'reverse',
  'steerLeft',
  'steerRight',
  'slewLeft',
  'slewRight',
  'boomUp',
  'boomDown',
  'dipperIn',
  'dipperOut',
  'bucketCurl',
  'bucketDump',
] as const;
export type ControlAction = (typeof CONTROL_ACTIONS)[number];

/** KeyboardEvent.code → action. Config-overridable in the console. */
export const DEFAULT_KEY_BINDINGS: Readonly<Record<string, ControlAction>> = {
  KeyW: 'forward',
  KeyS: 'reverse',
  KeyA: 'steerLeft',
  KeyD: 'steerRight',
  KeyQ: 'slewLeft',
  KeyE: 'slewRight',
  KeyI: 'boomUp',
  KeyK: 'boomDown',
  KeyJ: 'dipperIn',
  KeyL: 'dipperOut',
  KeyU: 'bucketCurl',
  KeyO: 'bucketDump',
};

export interface CommandVector {
  drive: { left: number; right: number };
  slew: number;
  arm: { boom: number; dipper: number; bucket: number };
}

export const ZERO_VECTOR: CommandVector = Object.freeze({
  drive: Object.freeze({ left: 0, right: 0 }),
  slew: 0,
  arm: Object.freeze({ boom: 0, dipper: 0, bucket: 0 }),
});

function axis(active: ReadonlySet<ControlAction>, pos: ControlAction, neg: ControlAction): number {
  // Opposing keys cancel to 0 rather than "last one wins": predictable, and
  // a stuck key can always be overridden to a stop.
  return (active.has(pos) ? 1 : 0) - (active.has(neg) ? 1 : 0);
}

/**
 * Translate the set of currently-held actions into a full command vector.
 * Simultaneous actions compose: e.g. forward+steerLeft+boomUp all apply.
 */
export function actionsToVector(active: ReadonlySet<ControlAction>): CommandVector {
  const throttle = axis(active, 'forward', 'reverse');
  const steer = axis(active, 'steerRight', 'steerLeft');
  return {
    drive: mixArcade(throttle, steer),
    slew: axis(active, 'slewRight', 'slewLeft'),
    arm: {
      boom: axis(active, 'boomUp', 'boomDown'),
      dipper: axis(active, 'dipperOut', 'dipperIn'),
      bucket: axis(active, 'bucketCurl', 'bucketDump'),
    },
  };
}

/**
 * Arcade mix for differential drive: throttle ± steer per track, clamped.
 * steer with zero throttle spins in place; steer while driving arcs the turn.
 */
export function mixArcade(throttle: number, steer: number): { left: number; right: number } {
  return {
    left: clampRate(throttle + steer),
    right: clampRate(throttle - steer),
  };
}

/** Map held key codes through bindings to the active-action set. */
export function keysToActions(
  heldCodes: ReadonlySet<string>,
  bindings: Readonly<Record<string, ControlAction>> = DEFAULT_KEY_BINDINGS,
): Set<ControlAction> {
  const active = new Set<ControlAction>();
  for (const code of heldCodes) {
    const action = bindings[code];
    if (action) active.add(action);
  }
  return active;
}

export function vectorIsZero(v: CommandVector): boolean {
  return (
    v.drive.left === 0 &&
    v.drive.right === 0 &&
    v.slew === 0 &&
    v.arm.boom === 0 &&
    v.arm.dipper === 0 &&
    v.arm.bucket === 0
  );
}

/** Assemble the wire frame for one tick. */
export function buildControlFrame(
  vector: CommandVector,
  seq: number,
  estop: boolean,
  now: number,
): ControlFrame {
  return {
    v: 1,
    type: 'control',
    seq,
    t: now,
    estop,
    drive: { left: vector.drive.left, right: vector.drive.right },
    slew: vector.slew,
    arm: { ...vector.arm },
  };
}
